import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DataTable,
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';
import { Skeleton } from '@/components/ui/skeleton';

const SKELETON_ROW_COUNT = 8;
/** Mirrors the data column widths in BrowsePatientsTableBody. */
const CELL_WIDTHS = ['w-32', 'w-16', 'w-24', 'w-10', 'w-28', 'w-20', 'w-16'] as const;

/**
 * Layout-stable placeholder for the BrowsePatients page. Mirrors the
 * filters bar, table toolbar, table header, and 8 data rows of
 * `BrowsePatientsPanel` so the pending → loaded transition does not
 * reflow the page or flash between two layouts. Reuses the real
 * `DataTable*` primitives so column widths can't drift out of sync
 * with the real table.
 */
export function BrowsePatientsSkeleton(): ReactElement {
  const { t } = useTranslation();

  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={t('routes.browsePatients.loading')}
      className="space-y-4"
    >
      <div className="flex flex-wrap items-end gap-3">
        <Skeleton className="h-9 w-full max-w-sm flex-1 min-w-[180px]" />
        <div className="space-y-1 min-w-[140px]">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-9 w-full" />
        </div>
        <div className="space-y-1 min-w-[160px]">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-9 w-full" />
        </div>
      </div>

      <DataTable>
        <div
          className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-white px-3 py-2.5 text-xs text-muted-foreground dark:bg-card sm:px-4"
          aria-hidden="true"
        >
          <div className="flex items-center gap-2">
            <Skeleton className="h-3 w-10" />
            <Skeleton className="h-8 w-14" />
            <Skeleton className="h-3 w-16" />
          </div>
          <div className="flex items-center gap-1">
            <Skeleton className="h-3 w-24 mr-2" />
            <Skeleton className="h-8 w-8 rounded-md" />
            <Skeleton className="h-8 w-8 rounded-md" />
          </div>
        </div>

        <DataTableTable>
          <DataTableThead>
            <DataTableHeaderRow>
              <DataTableTh widthClassName="w-[22%] min-w-[7rem]">
                <Skeleton className="h-3 w-20" />
              </DataTableTh>
              <DataTableTh widthClassName="w-24">
                <Skeleton className="h-3 w-10" />
              </DataTableTh>
              <DataTableTh widthClassName="w-32">
                <Skeleton className="h-3 w-16" />
              </DataTableTh>
              <DataTableTh className="w-20">
                <Skeleton className="h-3 w-8" />
              </DataTableTh>
              <DataTableTh className="min-w-[7rem] max-w-[10rem] md:min-w-32">
                <Skeleton className="h-3 w-16" />
              </DataTableTh>
              <DataTableTh className="min-w-0" widthClassName="min-w-24">
                <Skeleton className="h-3 w-20" />
              </DataTableTh>
              <DataTableTh
                className="w-32"
                widthClassName="w-32"
                align="right"
              >
                <Skeleton className="h-3 w-16 ml-auto" />
              </DataTableTh>
            </DataTableHeaderRow>
          </DataTableThead>
          <DataTableTbody>
            {Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIdx) => (
              <DataTableTr key={`skeleton-${String(rowIdx)}`}>
                {CELL_WIDTHS.map((width, cellIdx) => (
                  <DataTableTd
                    key={cellIdx}
                    align={cellIdx === CELL_WIDTHS.length - 1 ? 'right' : 'left'}
                  >
                    <Skeleton
                      className={`h-3.5 ${width} ${
                        cellIdx === CELL_WIDTHS.length - 1 ? 'ml-auto' : ''
                      }`}
                    />
                  </DataTableTd>
                ))}
              </DataTableTr>
            ))}
          </DataTableTbody>
        </DataTableTable>
      </DataTable>
    </div>
  );
}

